import { useState, useEffect, useRef } from "react"
import { ChevronsUpDown, Check, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
} from "@/components/ui/command"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"

import { environment } from "@/env/env.prod"
import { apiHeaders } from "@/Auth/api-headers."

// ─────────────────────────────────────────────────────────────────────────
// Shared searchable subject-teacher picker — used both for assigning a
// subject teacher in the timetable grid and for picking whose schedule to
// view in Teaching Schedule. A Popover+Command combo backed by the faculty
// directory (user/fetchUsers, designation=Teachers), same debounced-
// server-search pattern as SubjectCombobox. Faculty search/assignment is
// optional — a timetable slot is valid with no teacher at all.
//
// Shows each result's profile picture (already resolved to a signed
// storage URL server-side by UserManagementService — nothing new to fetch
// here) so two people with the same or similar name are easy to tell apart
// by face, same as TeacherSelectionDialog already does for coordinator
// assignment. Pagination is an explicit "Load more" click rather than
// scroll-triggered — a cramped popover makes scroll-to-load easy to miss.
// ─────────────────────────────────────────────────────────────────────────

export type TeacherOption = { uid: string; name: string; employee_id: string; email: string; profile_pic?: string | null }

function initialsFor(name: string) {
    return name.split(" ").map(n => n[ 0 ]).join("").substring(0, 2).toUpperCase()
}

type Props = {
    value: string
    onSelect: (teacher: TeacherOption) => void
    placeholder?: string
}

export function TeacherCombobox({ value, onSelect, placeholder = "Search & assign a teacher…" }: Props) {
    const [ open, setOpen ] = useState(false)
    const [ query, setQuery ] = useState("")
    const [ results, setResults ] = useState<TeacherOption[]>([])
    const [ loading, setLoading ] = useState(false)
    const [ loadingMore, setLoadingMore ] = useState(false)
    const [ nextToken, setNextToken ] = useState<string | null>(null)
    const [ hasMore, setHasMore ] = useState(false)
    const loadingRef = useRef(false) // guards against overlapping fetches

    const fetchTeachers = async (q: string, cursor: string | null, reset: boolean) => {
        if (loadingRef.current) return
        loadingRef.current = true
        if (reset) setLoading(true)
        else setLoadingMore(true)

        try {
            const params = new URLSearchParams({
                designation: "Teachers",
                searchedName: q,
                pageSize: "20",
                ...(cursor ? { lastPageSnap: cursor } : {}),
            })
            const res = await fetch(`${environment.BASE_URL}/user/fetchUsers?${params}`, {
                headers: apiHeaders() as HeadersInit,
            })
            const data = await res.json()
            if (data.success) {
                const incoming = (data.users || []) as TeacherOption[]
                setResults(prev => reset ? incoming : [ ...prev, ...incoming ])
                setNextToken(data.lastPageSnap ?? null)
                setHasMore(!!data.lastPageSnap)
            } else if (reset) {
                setResults([])
                setHasMore(false)
            }
        } catch {
            if (reset) setResults([])
            setHasMore(false)
        } finally {
            loadingRef.current = false
            setLoading(false)
            setLoadingMore(false)
        }
    }

    useEffect(() => {
        if (!open) return
        const t = setTimeout(() => fetchTeachers(query, null, true), 300)
        return () => clearTimeout(t)
    }, [ query, open ])

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between font-normal"
                >
                    <span className="truncate">{value || placeholder}</span>
                    <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                <Command
                    shouldFilter={false}
                    className="**:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-input]]:text-sm"
                >
                    <CommandInput
                        placeholder="Search by name…"
                        value={query}
                        onValueChange={setQuery}
                        onClear={query ? () => setQuery("") : undefined}
                    />
                    <CommandList>
                        <CommandEmpty>{loading ? "Searching…" : "No faculty found."}</CommandEmpty>
                        <CommandGroup>
                            {results.map(t => (
                                <CommandItem
                                    key={t.uid}
                                    value={t.uid}
                                    onSelect={() => { onSelect(t); setOpen(false) }}
                                >
                                    <Check className={cn("mr-2 h-4 w-4 shrink-0", value === t.name ? "opacity-100" : "opacity-0")} />
                                    <Avatar className="h-6 w-6 mr-2 shrink-0 border border-border/40">
                                        <AvatarImage src={t.profile_pic ?? ''} />
                                        <AvatarFallback className="text-[9px] font-bold bg-muted uppercase">
                                            {initialsFor(t.name)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="min-w-0">
                                        <div className="font-medium truncate">{t.name}</div>
                                        <div className="text-xs text-muted-foreground truncate">{t.email}</div>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>

                        {hasMore && (
                            <div className="p-2 border-t">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="w-full text-xs gap-1.5"
                                    disabled={loadingMore}
                                    onClick={() => fetchTeachers(query, nextToken, false)}
                                >
                                    {loadingMore && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                    {loadingMore ? "Loading…" : "Load more"}
                                </Button>
                            </div>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
